import React, { useState, useEffect, useRef } from "react";
import { IoIosArrowDown } from "react-icons/io";

const ComboBox = ({
    options = [],
    findedValue,
    placeholder,
    onSelect,
    onInputChange,
    className,
    inputClassName,
    onKeyDown,
    comboRef,
    onClick,
    onBlur,
    onFocus,
    id,
    inlineStyles,
    inputType = "text",
    readOnly = false,
    disabled = false,
    dropdownWidth = "100%",
    inputPadding = "2px" 
}) => {
    const [inputValue, setInputValue] = useState(findedValue || "");
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);

    const clearInput = () => {
        setInputValue("");
    };
    
    // Expose clearInput function through comboRef reference
    useEffect(() => {
        if (comboRef.current) {
            comboRef.current.clearInput = clearInput;
        }
    }, [comboRef]);

    useEffect(() => {
        setInputValue(findedValue || "");
    }, [findedValue]);

    const hasSelected = useRef(false);

    useEffect(() => {
        if (findedValue && options) {
            const foundOption = options.find(
                (option) => {
                    const optionValue = option?.[1];
                    return optionValue != null && optionValue.toString() === findedValue.toString()
                }
            );

            if (foundOption && !hasSelected.current) {
                const optionValue = foundOption?.[1];
                setInputValue(optionValue != null ? optionValue.toString() : "");
                handleSelectOption(foundOption);
                hasSelected.current = true;
            }
        } else {
            hasSelected.current = false;
        }
    }, [findedValue, options]);

    useEffect(() => {
        if (!inputValue) {
            setFilteredOptions(options);
        }
    }, [options]);

    // Helper function to safely get option value as string
    const getOptionValue = (option) => {
        if (!option || option.length < 2) return "";
        const value = option[1];
        return value != null ? value.toString() : "";
    };

    const handleSelectOption = (option) => {
        const optionValue = getOptionValue(option);
        setInputValue(optionValue);
        setIsOpen(false);
        onSelect && onSelect(option);
        onInputChange && onInputChange(option);
    };

    const handleInputChange = (event) => {
        const value = event.target.value;

        setInputValue(value);

        // Filter options based on the entered value
        const filtered = options.filter((option) => {
            if (!option) return false;
            
            return option.some(
                (optValue) => {
                    if (optValue == null) return false;
                    return optValue.toString().toLowerCase().includes(value.toLowerCase())
                }
            );
        });
        setFilteredOptions(filtered);
        // Call the onInputChange prop with the new input value
        onInputChange && onInputChange(value);

        // Always open the dropdown when there is input
        setIsOpen(value !== "");
    };

    const handleKeyDown = (event) => {
        if (isOpen) {
            switch (event.key) {
                case "ArrowDown": {
                    event.preventDefault(); // Prevent the default scrolling behavior
                    const currentIndex =
                        filteredOptions &&
                        filteredOptions.findIndex(
                            (option) => getOptionValue(option) === inputValue
                        );
                    const nextIndex = (currentIndex + 1) % filteredOptions.length;
                    const nextOption = filteredOptions[nextIndex];
                    if (nextOption) {
                        setInputValue(getOptionValue(nextOption));
                        // Scroll the dropdown after the 5th option
                        if (nextIndex >= 0) {
                            const dropdownContainer =
                                document.querySelector(".combobox_dropdown");
                            const optionElement = document.querySelector(
                                `.dropdown_option:nth-child(${nextIndex + 1})`
                            );
                            if (dropdownContainer && optionElement) {
                                dropdownContainer.scrollTop = optionElement.offsetTop;
                            }
                        }
                    }
                    break;
                }
                case "ArrowUp": {
                    event.preventDefault(); // Prevent the default scrolling behavior
                    const currentIndex =
                        filteredOptions &&
                        filteredOptions.findIndex(
                            (option) => getOptionValue(option) === inputValue
                        );
                    const prevIndex =
                        (currentIndex - 1 + filteredOptions.length) %
                        filteredOptions.length;
                    const prevOption = filteredOptions[prevIndex];
                    if (prevOption) {
                        setInputValue(getOptionValue(prevOption));
                        // Scroll the dropdown to ensure the previous option is visible
                        const dropdownContainer =
                            document.querySelector(".combobox_dropdown");
                            const optionElement = document.querySelector(
                                `.dropdown_option:nth-child(${prevIndex + 1})`
                            );
                        if (dropdownContainer && optionElement) {
                            dropdownContainer.scrollTop = optionElement.offsetTop;
                        }
                    }
                    break;
                }
                case "Enter": {
                    event.preventDefault(); // Prevent form submission
                    const selectedOption =
                        filteredOptions &&
                        filteredOptions.find(
                            (option) => getOptionValue(option) === inputValue
                        );
                    if (selectedOption) {
                        handleSelectOption(selectedOption);
                    }
                    setIsOpen(false);
                    break;
                }
                case "Escape":
                    setIsOpen(false);
                    break;
                default:
                    break;
            }
        }
        // Pass the event to the parent component
        onKeyDown && onKeyDown(event);
    };

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const inputElement = comboRef.current;

            if (isOpen && inputElement && !inputElement.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("click", handleOutsideClick);

        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [isOpen]);

    /////styles - keeping original border styles exactly as they were /////
    const styles = {
        dropdown_option: {
            padding: "8px",
            cursor: "pointer",
            fontWeight: "350",
            fontSize: "11px",
            fontFamily: '"Nunito Sans", sans-serif,Roboto, Arial, Helvetica',
            textAlign: "left",
        },
        selectedcomboopt: {
            backgroundColor: "#007BFF",
            color: "white",
        },
        "dropdown_option:hover": {
            backgroundColor: "#f0f0f0",
        },

        combo_box_input: {},
        combo_box_input_field: {
            // padding: '8px',
            cursor: "pointer",
        },
        combo_box_container: {
            position: "relative",
            display: "flex",
            alignItems: "center",
            // justifyContent: "center",
        },

        combo_box_input: {
            position: "relative",
            width: "100%",
            height: "100%",
        },
        combo_box_input_field: {
            margin: 0,
            boxSizing: "border-box", // Changed to border-box to include padding in width/height
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            fontFamily: '"Nunito Sans", sans-serif, Roboto, Arial, Helvetica',
            cursor: "pointer",
            outline: "none",
            padding: inputPadding, // Added padding here - can be customized
        },

        "arrow_icon.rotated": {
            transform: "translateY(-50%) rotate(180deg)",
        },

        combobox_dropdown: {
            width: dropdownWidth,
            border: "1px solid #ccc", // Keeping original border
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            maxHeight: "150px",
            overflowY: "auto",
            backgroundColor: "white",
            position: "relative", // Keeping original
            zIndex: "35",
        },
    };

    const handleBlur = (event) => {
        setTimeout(() => {
            setIsOpen(false);
        }, 100);

        onBlur && onBlur(event);
    };

    const handleFocus = () => {
        if (readOnly || disabled) return;
        setFilteredOptions(options);
        setIsOpen(true);
        onFocus && onFocus();
    };

    return (
        <div
            className={`combo_box_container ${className}`}
            style={styles.combo_box_container}
        >
            <div className="combo_box_input" style={styles.combo_box_input}>
                <input
                    id={id}
                    ref={comboRef}
                    type={inputType}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className={`combo_box_input_field ${inputClassName}`}
                    style={{ 
                        ...styles.combo_box_input_field, 
                        ...inlineStyles,
                        // Ensure padding from prop overrides any inline styles
                        padding: inputPadding,
                        boxSizing: "border-box" // Ensure padding is included in dimensions
                    }}
                    onBlur={(e) => handleBlur(e)}
                    onFocus={handleFocus}
                    onClick={onClick}
                    readOnly={readOnly}
                    disabled={disabled}
                />

                {isOpen && (
                    <div style={styles.combobox_dropdown} className="combobox_dropdown">
                        {filteredOptions &&
                            filteredOptions.map((option, index) => {
                                const optionValue = getOptionValue(option);
                                return (
                                    <div
                                        key={index}
                                        style={{
                                            ...styles.dropdown_option,
                                            ...(optionValue === inputValue
                                                ? styles.selectedcomboopt
                                                : {}),
                                        }}
                                        className={`dropdown_option ${optionValue === inputValue
                                                ? "selectedcomboopt"
                                                : ""
                                            }`}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleSelectOption(option);
                                        }}
                                    >
                                        {optionValue || ""}
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComboBox;